import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiErrorMessage } from '../../../i18n';
import { api } from '../../../lib/api';

type UseRemoveDirectItemArgs = {
  organisationId: string;
  screenId: string;
  playlistId: string;
  onCleared?: () => void;
  onError?: (message: string) => void;
};

/**
 * Remove one item from this display's direct show and republish.
 * Last item clears the screen assignment entirely.
 */
export function useRemoveDirectItem({
  organisationId,
  screenId,
  playlistId,
  onCleared,
  onError,
}: UseRemoveDirectItemArgs) {
  const { t } = useTranslation('displays');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const items = await api.listPlaylistItems(organisationId, playlistId);
      if (items.length <= 1) {
        await api.clearScreenAssignment(organisationId, screenId);
        return { cleared: true as const };
      }
      await api.removePlaylistItem(organisationId, playlistId, itemId);
      await api.publishPlaylist(organisationId, playlistId);
      return { cleared: false as const };
    },
    onSuccess: async (result) => {
      void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
      void queryClient.invalidateQueries({
        queryKey: ['screen-assignment', organisationId, screenId],
      });
      void queryClient.invalidateQueries({ queryKey: ['playlist-items', playlistId] });
      void queryClient.invalidateQueries({ queryKey: ['playlist-version', playlistId] });
      void queryClient.invalidateQueries({
        queryKey: ['playlist-publish-state', organisationId, playlistId],
      });
      if (result.cleared) onCleared?.();
    },
    onError: (err) => {
      onError?.(apiErrorMessage(err, t('manage.errors.removeItem')));
    },
  });
}
